"use client";
import React, { useState, useEffect, useTransition } from "react";
import { ServiceModal } from "./serviceModal";
import { usePathname, useRouter } from "next/navigation";
import { CategoryItems } from "./categoryItems";
import { Surcharge } from "@/components/surcharge/surcharge";
import { Category } from "@/dto/category";
import { ServicesPageSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { Menu } from "@/dto/menu";
import { GetMerchantPreferencesResponse } from "@/dataLayer/getMerchantPreferences";
import { GetMerchantPaymentConfigResponse } from "@/dataLayer/getMerchantPaymentConfig";
import { Item } from "@/dto/item";

interface ServicesPageProps {
  merchantName: string;
  merchantColor: string;
  searchParams: any;
  isCheckoutEnabled: boolean;
  menu: Menu | null;
  serviceCategories: Category[];
  itemTemplate?: string;
  merchantId: string;
  isAcceptingAppointments: boolean;
  merchantPreferences: GetMerchantPreferencesResponse;
  merchantPaymentConfig: GetMerchantPaymentConfigResponse;
  isLoading: boolean;
}

export const ServicesPage = ({
  merchantName,
  merchantColor,
  searchParams,
  isCheckoutEnabled,
  menu,
  serviceCategories,
  itemTemplate = "description focused",
  merchantId,
  isAcceptingAppointments,
  merchantPreferences,
  merchantPaymentConfig,
  isLoading,
}: ServicesPageProps) => {
  const query = searchParams.query || "";
  const router = useRouter();
  const pathname = usePathname();
  const [selectedService, setSelectedService] = useState<Item | null>(null);
  const [modifiers, setModifiers] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const serviceId = pathname?.split("/services/")[1];
    if (serviceId) {
      const matchingService = menu?.items.find(
        (item: any) => item.id === serviceId
      );
      if (matchingService) {
        setSelectedService(matchingService);
        setIsModalOpen(true);
        const modifiers = getMatchingModifiers({
          modifiers: menu?.modifiers,
          item: matchingService,
          modifierGroups: menu?.modifierGroups,
        });
        setModifiers(modifiers);
      }
    } else {
      setIsModalOpen(false);
      setSelectedService(null);
    }
  }, [pathname, menu?.items]);

  const handleItemClick = (item: any) => {
    router.replace(`/services/${item.id}`);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    router.replace("/services/all");
  };

  const sortedCategories = serviceCategories
    .filter((cat: Category) => cat.id !== "uncategorised")
    .sort(
      (a: any, b: any) =>
        (a as { sortOrder: number })?.sortOrder -
        (b as { sortOrder: number })?.sortOrder
    );
  const itemsMap =
    menu && Object.fromEntries(menu?.items.map((item: any) => [item.id, item]));

  const uncategorizedItems = menu?.items.filter(
    (item: any) => item.categoryIds.length === 0 && item.itemType === "SERVICE"
  );

  if (isLoading && !isModalOpen) {
    return <ServicesPageSkeleton itemTemplate={itemTemplate} />;
  }

  if (isLoading && isModalOpen) {
    return (
      <>
        <ServicesPageSkeleton itemTemplate={itemTemplate} />
        <div className="ReactModal__Overlay ReactModal__Overlay--after-open fixed w-screen h-screen inset-0 bg-black bg-opacity-75 z-50"></div>
      </>
    );
  }

  return (
    <>
      <div className="px-4 sm:px-8 pb-9 w-full scroll-smooth">
        <div className="sm:hidden text-center">
          {merchantPaymentConfig && merchantPaymentConfig.surcharging?.rate && (
            <Surcharge
              surchargeRate={merchantPaymentConfig.surcharging?.rate}
            />
          )}
        </div>
        {sortedCategories.map((category: any) => (
          <CategoryItems
            {...{
              category,
              itemTemplate,
              itemsMap,
              query,
              handleItemClick,
            }}
            merchantName={merchantName}
            key={category?.id}
          />
        ))}
        {uncategorizedItems && uncategorizedItems.length > 0 && (
          <CategoryItems
            {...{
              category: {
                id: "Uncategorized",
                name: "Uncategorized",
                items: uncategorizedItems,
              },
              itemTemplate,
              itemsMap,
              query,
              handleItemClick,
            }}
          />
        )}
        {isModalOpen && selectedService && (
          <ServiceModal
            isCheckoutEnabled={isCheckoutEnabled}
            merchantName={merchantName}
            merchantColor={merchantColor}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            service={selectedService}
            menu={menu}
            merchantId={merchantId}
            modifiers={modifiers}
            isAcceptingAppointments={isAcceptingAppointments}
            merchantPreferences={merchantPreferences.appointmentPref}
          />
        )}
      </div>
      {isModalOpen && (
        <div className="ReactModal__Overlay ReactModal__Overlay--after-open fixed w-screen h-screen inset-0 bg-black bg-opacity-75 z-50"></div>
      )}
    </>
  );
};

const getMatchingModifiers = ({ modifiers, item, modifierGroups }: any) => {
  return modifiers.filter((m: any) =>
    item.modifierGroupIds?.includes(m.groupId)
  );
};
